import {
  booleanAttribute,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  inject,
  Input,
  input,
  model,
  OnInit,
  output,
  OutputEmitterRef,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { PopoverModule } from 'primeng/popover';
import { DmButtonLibComponent } from '@everymatrix-base-components/dm-button-lib';
import {
  GlobalMessageService,
  ReportTemplate,
  TemplateService,
  SessionService
} from '@everymatrix-base-components/utils';
import { take, tap } from 'rxjs';
import { DeleteTemplateModalComponent } from '../delete-template-modal/delete-template-modal';
import { InputText } from 'primeng/inputtext';
import { NgClass, NgStyle } from '@angular/common';
import { TooltipModule } from 'primeng/tooltip';
import { SendSettingsModalComponent } from '../send-settings-modal/send-settings-modal';
import { ProgressSpinner } from 'primeng/progressspinner';
import { DomainsOverloadModalComponent } from '../domains-overload-modal/domains-overload-modal';

@Component({
  selector: 'lib-template-select-modal',
  standalone: true,
  imports: [
    PopoverModule,
    FormsModule,
    DmButtonLibComponent,
    DeleteTemplateModalComponent,
    InputText,
    NgClass,
    TooltipModule,
    SendSettingsModalComponent,
    NgStyle,
    ProgressSpinner,
    DomainsOverloadModalComponent,
  ],
  templateUrl: './template-select-modal.html',
  styleUrls: ['./template-select-modal.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TemplateSelectModalComponent implements OnInit {
  selectedTemplateId!: string;
  searchQuery = signal<string>('');
  runReportEmitter = output();
  csvLoadEmitter: OutputEmitterRef<ReportTemplate> = output();
  templateService = inject(TemplateService);
  messageService = inject(GlobalMessageService);
  sessionService = inject(SessionService);
  cdr = inject(ChangeDetectorRef);
  selectedTemplate!: ReportTemplate;
  reportId = input.required<number>();
  showDeleteConfirmation = model(false);
  templateCsvLoadingConfig = model({ templateId: '', isLoading: false });
  showSendingModal = model(false);
  templateSignalList = this.templateService.templatesList;
  templateDeleteLoading = model(false);
  loading = signal(false);
  templateCsvLoadingIds = new Set<string>();
  templateDuplicateLoadingIds = new Set<string>();
  @Input() domainLookups: any[] | undefined;
  @Input() selectedDomainsKey = 'merchant_name';
  domainsOverloadVisible = signal(false);
  overloadCheckAction: 'Run' | 'Download' | '' = '';
  overloadCheckTemplate: ReportTemplate | null = null;
  filteredFieldsSignal = computed(() => {
    const query = this.searchQuery().trim().toLowerCase();
    const list = this.templateSignalList() as ReportTemplate[] || [];
    if (!query) {
      return list;
    }

    return list.filter(item =>
      !item.is_temporary &&
      item.template_name.toLowerCase().includes(query)
    );
  });
  @Input({ transform: booleanAttribute }) isShowSendSettings = false;

  constructor() {
    effect(() => {
      this.selectedTemplateId =
        this.templateService.template()?.template_id || '';
    });
    effect(() => {
      this.templateCsvLoadingIds.delete(
        this.templateCsvLoadingConfig().templateId
      );
    });
  }

  ngOnInit() {
    this.loading.set(true);
    this.templateService
      .getAllTemplates(this.reportId())
      .pipe(
        take(1),
        tap((templates) => {
          this.templateService.templatesList.set(
            templates
          );
        })
      )
      .subscribe(() => this.loading.set(false));
  }

  deleteTemplate() {
    this.showDeleteConfirmation.set(true);
    this.templateService
      .deleteTemplate(this.selectedTemplateId)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.templateDeleteLoading.set(false);
          this.showDeleteConfirmation.set(false);

          this.templateSignalList.update((arr) => {
            return (arr as ReportTemplate[]).filter(
              (t) => t.template_id !== this.selectedTemplateId
            );
          });

          if (
            !this.templateSignalList()?.length ||
            this.selectedTemplateId ===
              this.templateService.template()?.template_id
          ) {
            this.editTemplate(null);
          }

          this.messageService.showSuccessMessage({
            detail: 'Template is deleted successfully!',
          });
        },
        error: () => {
          this.templateDeleteLoading.set(false);
          this.messageService.showErrorMessage({
            detail: 'Failed to delete template.',
          });
        },
      });
  }

  duplicateTemplate(template: ReportTemplate, index: number) {
    if(this.templateDuplicateLoadingIds.has(template.template_id)) return;
    this.templateDuplicateLoadingIds.add(template.template_id);
    this.templateService
      .saveTemplate(
        `${template.template_name}_duplicated`,
        template.template_desc,
        template.input_params,
        template.report_id,
        null,
        false
      )
      .pipe(take(1))
      .subscribe({
        next: (duplicated) => {
          this.templateSignalList.update((arr) => {
            const list = [...(arr as ReportTemplate[])];
            list.splice(index + 1, 0, duplicated);
            return list;
          });
          this.templateDuplicateLoadingIds.delete(template.template_id);
          this.messageService.showSuccessMessage({
            detail: 'Template is duplicated successfully!',
          });
        },
        error: (err) => {
          this.templateDuplicateLoadingIds.delete(template.template_id);
          this.messageService.showErrorMessage({
            detail: `Template is not duplicated. ${err}`,
          });
          this.cdr.detectChanges();
        },
      });
  }

  editTemplate(template: ReportTemplate | null): void {
    if (template) {
      if (
        template.template_id !== this.templateService.template()?.template_id
      ) {
        this.templateService.setSearchParams({
          modificationId: null,
          templateName: null,
          templateId: template.template_id,
        });
      }

      this.templateService.template.set(template);
      this.templateService.templateChangedSubject.next(template);
    } else {
      this.templateService.setSearchParams({
        modificationId: null,
        templateName: null,
        templateId: null,
      });
      this.templateService.template.set(null);
      this.templateService.templateChangedSubject.next(null);
    }
  }

  runReport(template: ReportTemplate): void {
    if (this.templateService.template()?.template_id !== template.template_id) {
      this.templateService.setSearchParams({
        modificationId: null,
        templateName: null,
        templateId: template.template_id,
      });
      this.templateService.templateChangedSubject.next(template);
      this.templateService.template.set(template);
    }
    this.runReportEmitter.emit();
  }

  runCsv(template: ReportTemplate): void {
    this.templateCsvLoadingIds.add(template.template_id);
    this.csvLoadEmitter.emit(template);
  }

  protected domainsOverloadConfirm(event: {confirm: boolean, isSaveMode: boolean}): void {
    const {confirm} = event
    if(confirm && this.overloadCheckTemplate) {
      this.overloadCheckAction == 'Run' ? this.runReport(this.overloadCheckTemplate) : this.runCsv(this.overloadCheckTemplate);
    }
  }

  protected runOverloadCheck(template: ReportTemplate, action: 'Run' | 'Download') {
    if(action == 'Download' && this.templateCsvLoadingIds.has(template.template_id)) return;
    if (this.isAllDomainsSelected(template)) {
      this.overloadCheckTemplate = template;
      this.overloadCheckAction = action;
      this.domainsOverloadVisible.set(true);
    } else {
      action == 'Run' ? this.runReport(template) : this.runCsv(template);
    }
  }

  protected getTemplateSelectedDomains(template: ReportTemplate): number {
    return template.input_params && template.input_params.fieldValues && template.input_params.fieldValues[this.selectedDomainsKey] && template.input_params.fieldValues[this.selectedDomainsKey].length || 0;
  }

  protected isAllDomainsSelected(template: ReportTemplate): boolean {
    if (!this.domainLookups || !this.domainLookups.length || this.domainLookups.length == 1 || this.sessionService.sessionData?.Domains && this.sessionService.sessionData.Domains != '1000') return false;
    return this.domainLookups.length === this.getTemplateSelectedDomains(template);
  }

  onSearchChange(event: Event) {
    const inputElement = event.target as HTMLInputElement;
    this.searchQuery.set(inputElement.value);
  }
}
